export const EMPTY = '—';

export const DELTA_ARROWS = { up: '↑', down: '↓', flat: '→' };

const MINUS = '−';

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_ONLY = /^(\d{4})-(\d{2})$/;

export const isNumber = (value) => typeof value === 'number' && isFinite(value);

const pad2 = (value) => (value < 10 ? '0' : '') + value;

const localeNumber = (value, digits) =>
  value.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits });

export const formatInteger = (value) => {
  if (!isNumber(value)) return EMPTY;
  const rounded = Math.round(value);
  return localeNumber(rounded === 0 ? 0 : rounded, 0);
};

export const formatDecimal = (value, digits) => {
  if (!isNumber(value)) return EMPTY;
  const places = isNumber(digits) ? digits : 1;
  const factor = Math.pow(10, places);
  const rounded = Math.round(value * factor) / factor;
  return localeNumber(rounded === 0 ? 0 : rounded, places);
};

export const formatPercent = (value, digits) => {
  if (!isNumber(value)) return EMPTY;
  return formatDecimal(value, isNumber(digits) ? digits : 1) + '%';
};

export const formatDays = (value) => {
  if (!isNumber(value)) return EMPTY;
  const whole = Math.round(value) === value;
  const text = whole ? formatInteger(value) : formatDecimal(value, 1);
  return text + (whole && Math.abs(value) === 1 ? ' dia' : ' dias');
};

export const formatValue = (value, unit) => {
  if (unit === 'pct') return formatPercent(value);
  if (unit === 'ratio') return formatDecimal(value, 2);
  if (unit === 'days') return formatDays(value);
  return formatInteger(value);
};

export const deltaDirection = (deltaAbs) => {
  if (!isNumber(deltaAbs)) return null;
  if (deltaAbs > 0) return 'up';
  if (deltaAbs < 0) return 'down';
  return 'flat';
};

const signed = (value, text) => {
  if (value > 0) return '+' + text;
  if (value < 0) return MINUS + text;
  return text;
};

export const formatDeltaAbs = (deltaAbs, unit) => {
  if (!isNumber(deltaAbs)) return EMPTY;
  const size = Math.abs(deltaAbs);
  if (unit === 'pct') return signed(deltaAbs, formatDecimal(size, 1)) + ' p.p.';
  if (unit === 'ratio') return signed(deltaAbs, formatDecimal(size, 2));
  if (unit === 'days') return signed(deltaAbs, formatDays(size));
  return signed(deltaAbs, formatInteger(size));
};

export const formatDeltaPct = (deltaPct) => {
  if (!isNumber(deltaPct)) return EMPTY;
  return signed(deltaPct, formatPercent(Math.abs(deltaPct), 1));
};

export const formatDelta = (deltaAbs, deltaPct, unit) => {
  const direction = deltaDirection(deltaAbs);
  if (!direction) return EMPTY;
  const text = DELTA_ARROWS[direction] + ' ' + formatDeltaAbs(deltaAbs, unit);
  return isNumber(deltaPct) ? text + ' (' + formatDeltaPct(deltaPct) + ')' : text;
};

const ISO_TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?\s*(Z|z|[+-]\d{2}:?\d{2})?$/;

const ZONED = /[zZ]$|[+-]\d{2}:?\d{2}$/;

const zoneMinutes = (zone) => {
  if (!zone || zone === 'Z' || zone === 'z') return 0;
  const digits = zone.replace(':', '');
  const minutes = Number(digits.substring(1, 3)) * 60 + Number(digits.substring(3, 5));
  return digits.charAt(0) === '-' ? -minutes : minutes;
};

const fromIsoTimestamp = (value) => {
  const match = ISO_TIMESTAMP.exec(value);
  if (!match) return null;
  const millis = match[7] ? Number((match[7] + '00').substring(0, 3)) : 0;
  const utc = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]),
    match[6] ? Number(match[6]) : 0, millis);
  return new Date(utc - zoneMinutes(match[8]) * 60000);
};

export const toInstant = (value) => {
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string' || !value) return null;
  const trimmed = value.trim();
  const parsed = DATE_ONLY.test(trimmed) ? null : fromIsoTimestamp(trimmed);
  if (parsed) return isNaN(parsed.getTime()) ? null : parsed;
  const fallback = new Date(trimmed);
  return isNaN(fallback.getTime()) ? null : fallback;
};

const dayParts = (value) => {
  if (typeof value === 'string') {
    const match = DATE_ONLY.exec(value.substring(0, 10));
    if (match && (value.length === 10 || !ZONED.test(value))) {
      return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
    }
  }
  const date = toInstant(value);
  if (!date) return null;
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
};

export const formatDate = (value) => {
  const parts = dayParts(value);
  return parts ? pad2(parts.day) + '/' + pad2(parts.month) : EMPTY;
};

export const formatDateFull = (value) => {
  const parts = dayParts(value);
  return parts ? pad2(parts.day) + '/' + pad2(parts.month) + '/' + parts.year : EMPTY;
};

export const formatDateNear = (value, now) => {
  const parts = dayParts(value);
  if (!parts) return EMPTY;
  const reference = toInstant(now) || new Date();
  return parts.year === reference.getFullYear() ? formatDate(value) : formatDateFull(value);
};

export const formatTime = (value) => {
  const date = toInstant(value);
  return date ? pad2(date.getHours()) + ':' + pad2(date.getMinutes()) : EMPTY;
};

export const formatDateTime = (value) => {
  const date = toInstant(value);
  return date ? formatDate(date) + ' ' + formatTime(date) : EMPTY;
};

export const formatMonth = (value) => {
  if (typeof value !== 'string') return EMPTY;
  const match = MONTH_ONLY.exec(value.substring(0, 7));
  if (!match) return EMPTY;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return EMPTY;
  return MONTHS[month - 1] + '/' + match[1];
};

export const formatPeriod = (from, to) => {
  const start = dayParts(from);
  const end = dayParts(to);
  if (!start || !end) return EMPTY;
  if (start.year === end.year) return formatDate(from) + ' a ' + formatDateFull(to);
  return formatDateFull(from) + ' a ' + formatDateFull(to);
};

export const minutesSince = (value, now) => {
  const date = toInstant(value);
  const reference = toInstant(now) || new Date();
  if (!date) return null;
  return Math.max(0, Math.floor((reference.getTime() - date.getTime()) / 60000));
};

export const formatElapsed = (minutes) => {
  if (!isNumber(minutes)) return EMPTY;
  if (minutes < 1) return 'agora';
  if (minutes < 60) return 'há ' + Math.floor(minutes) + ' min';
  if (minutes < 1440) return 'há ' + Math.floor(minutes / 60) + ' h';
  const days = Math.floor(minutes / 1440);
  return 'há ' + days + (days === 1 ? ' dia' : ' dias');
};

export const formatDuration = (milliseconds) => {
  if (!isNumber(milliseconds)) return EMPTY;
  if (milliseconds < 1000) return formatInteger(milliseconds) + ' ms';
  if (milliseconds < 60000) return formatDecimal(milliseconds / 1000, 1) + ' s';
  return formatDecimal(milliseconds / 60000, 1) + ' min';
};

export const formatBoolean = (value) => {
  if (value === true) return 'Sim';
  if (value === false) return 'Não';
  return EMPTY;
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piFormat.js